import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  notificationProviderCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { notificationEndpoints } from "@/features/notification/api/endpoints";
import type {
  NotificationProviderCreateRequest,
  NotificationProviderDto,
} from "@/features/notification/api/dto";

/**
 * Crée une ligne de routage (`POST /NotificationProviders/add`, guide v2.3 §6.7).
 * `code` doit être unique et en MAJUSCULES ; `config` refuse toute clé évoquant
 * `token`/`password`/`key`. Rôles : flashpay-superadmin, flashpay-admin.
 */
export const useCreateNotificationProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["notification", "providers", "create"],
    mutationFn: (body: NotificationProviderCreateRequest) =>
      mutateData({
        url: notificationEndpoints.providers.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<NotificationProviderDto>,
    onSuccess: () =>
      invalidateAfterMutation(queryClient, notificationProviderCacheKeys(), {
        resetListPage: true,
      }),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateNotificationProvider failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "notification" }
      );
    },
  });
};
